
import { useCallback } from 'react';
import { FormEditorActionsOptions } from '../types';

/**
 * Form editor action utilities for import/export.
 * 
 * Returns pure results — consuming apps handle their own toast/notification layer.
 * 
 * @example
 * ```tsx
 * const actions = useFormEditorActions({ formConfig, setFormConfig });
 * 
 * // Export schema as JSON file download
 * actions.exportSchema();
 * 
 * // Import: wire to a file input's onChange
 * <input type="file" onChange={(e) => {
 *   const result = actions.importSchema(e);
 *   if (result.success) showToast('Imported!');
 *   else showToast(result.error, 'error');
 * }} />
 * ```
 */
export const useFormEditorActions = ({ formConfig, setFormConfig }: FormEditorActionsOptions) => {
  const exportSchema = useCallback(() => {
    const schemaExport = {
      schema: formConfig.schema,
      uiSchema: formConfig.uiSchema,
      title: formConfig.title,
      description: formConfig.description,
    };

    const blob = new Blob([JSON.stringify(schemaExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formConfig.title.replace(/\s+/g, '-').toLowerCase() || 'form'}-schema.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [formConfig]);

  const importSchema = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): { success: boolean; error?: string } => {
      const file = event.target.files?.[0];
      if (!file) return { success: false, error: 'No file selected' };

      // Note: FileReader is async, so we kick it off and let it resolve.
      // The consuming app should treat this as fire-and-forget with a callback pattern
      // or use the returned promise-like pattern.
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target?.result as string);

          if (imported.schema && imported.schema.type === 'object' && imported.schema.properties) {
            setFormConfig(prev => ({
              ...prev,
              schema: imported.schema,
              uiSchema: imported.uiSchema || {},
              title: imported.title || prev.title,
              description: imported.description || prev.description,
            }));
          } else {
            console.error('Invalid schema format during import');
          }
        } catch (error) {
          console.error('Failed to parse imported schema:', error);
        }
      };
      reader.readAsText(file);

      // Reset input
      event.target.value = '';
      return { success: true };
    },
    [setFormConfig]
  );

  return { exportSchema, importSchema };
};
